const TIMEOUT = 600;
import {
  Component,
  Prop,
  Event,
  EventEmitter,
  Method,
  State,
  Element,
  h
} from '@stencil/core';
import { Color, Size } from '../../interface';
import isBoolean from 'lodash-es/isBoolean';
import isObject from 'lodash-es/isObject';
import { TwoPosState } from './two-pos-controller-interface';
@Component({
  tag: 'lar-two-pos-controller',
  styleUrl: 'two-pos-controller.scss',
  shadow: true
})
export class TwoPosControllerNode {
  @Element() el!: HTMLLarAnalogValueElement;
  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'off';
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  @State() fullState: TwoPosState = {};
  @State() setPointEdit: number = 0;
  @State() loading: boolean = true;
  @State() step?: number;
  @State() min?: number;
  @State() max?: number;
  @State() manualcontrols?: boolean;

  private node!: HTMLLarNodeElement;
  private timeout: number;

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.output.emit({ command: 'get' });
  }

  /**
   * Larva error input
   */
  @Method()
  async error(data: any) {
    if (this.node) {
      this.node.error(data);
    }
    this.loading = false;
  }
  /**
   * Input Larva event message (see above)
   */
  @Method()
  async input(data: boolean | TwoPosState) {
    if (isBoolean(data)) {
      this.fullState.outputValue = data as boolean;
    } else if (isObject(data)) {
      this.fullState = data as TwoPosState;
      this.setPointEdit = this.fullState.setPoint;
      this.manualcontrols = !!this.fullState.manualControls;
      this.step = Number(this.fullState.step);
      this.min = Number(this.fullState.min);
      this.max = Number(this.fullState.max);
    }
    this.loading = false;
  }

  private changeSetpoint(increase: boolean) {
    if (this.loading) {
      return;
    }
    const step = (this.step || 1) * (increase ? 1 : -1);
    this.setPointEdit = this.setPointEdit + step;
    if (this.max !== undefined && this.setPointEdit > this.max) {
      this.setPointEdit = this.max;
    }
    if (this.min !== undefined && this.setPointEdit < this.min) {
      this.setPointEdit = this.min;
    }
    // tslint:disable-next-line: strict-boolean-conditions
    if (this.timeout) {
      window.clearTimeout(this.timeout);
      this.timeout = null;
    }
    this.timeout = window.setTimeout(() => {
      this.loading = true;
      this.output.emit({ command: 'set', setPoint: this.setPointEdit });
      this.timeout = null;
    }, TIMEOUT);
  }

  private changeMode(mode: string) {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.output.emit({ command: 'mode', mode });
  }

  render() {
    const value = String(this.fullState.inputValue || '');
    const componentProps = {
      color: this.colorInputs || this.color,
      min: this.min,
      max: this.max,
      step: this.step,
      mode: this.fullState.mode,
      manualcontrols: !!this.fullState.manualControls,
      setpoint: this.setPointEdit,
      disabled: this.loading,
      onChangemode: (ev: CustomEvent) => this.changeMode(ev.detail),
      onChangesetpoint: (ev: CustomEvent) => this.changeSetpoint(ev.detail),
    }
    return (
      <lar-node
        value={value}
        hideTitles={this.hideTitles}
        icon={this.icon}
        color={this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.colorModal}
        colorInputs={this.colorInputs}
        colorIconSmall={this.colorIconSmall}
        loading={this.loading}
        log={this.log}
        nodeSize={this.nodeSize}
        component="two-pos-controller-content"
        componentProps={componentProps}
      >
        <slot />
      </lar-node>
    );
  }
}
